import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusIndicator } from '@/components/ui/status-indicator';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/shared/EmptyState';
import {
  Search,
  FolderGit2,
  Database,
  GitBranch,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';

interface AdminProject {
  id: string;
  name: string;
  gitUrl: string | null;
  templateId: string | null;
  hasDatabase: boolean;
  hasForgejoRepo: boolean;
  forgejoRepoUrl: string | null;
  createdAt: string;
  user: {
    id: string;
    email: string;
    name: string | null;
    avatar: string | null;
  } | null;
  sessionCount: number;
  activeSessionCount: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function AdminProjectsPage() {
  const { t, i18n } = useTranslation(['adminProjects', 'common']);
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [refreshing, setRefreshing] = useState(false);

  const fetchProjects = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });

      if (search) {
        params.set('search', search);
      }

      const response = await fetch(`/api/admin/projects?${params}`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.data.projects);
        setPagination(data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error(t('adminProjects:errors.loadProjects'));
    }
  }, [pagination.page, pagination.limit, search, t]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchProjects();
      setLoading(false);
    };
    loadData();
  }, [fetchProjects]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchProjects();
    setRefreshing(false);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat(i18n.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(date));
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={t('adminProjects:title')}
          description={t('adminProjects:description')}
        />
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-14 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('adminProjects:title')}
        description={t('adminProjects:description')}
        action={
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`}
            />
            {t('adminProjects:actions.refresh')}
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-surface-0 border rounded-lg p-4">
          <div className="flex items-center gap-2">
            <FolderGit2 className="h-5 w-5 flex-shrink-0 text-primary" />
            <span className="text-sm text-muted-foreground">
              {t('adminProjects:stats.totalProjects')}
            </span>
          </div>
          <p className="text-2xl font-bold text-primary mt-2">
            {pagination.total}
          </p>
        </div>
        <div className="bg-surface-0 border rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 flex-shrink-0 text-success" />
            <span className="text-sm text-muted-foreground">
              {t('adminProjects:stats.withDatabase')}
            </span>
          </div>
          <p className="text-2xl font-bold text-success mt-2">
            {projects.filter((p) => p.hasDatabase).length}
          </p>
        </div>
        <div className="bg-surface-0 border rounded-lg p-4">
          <div className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 flex-shrink-0 text-warning" />
            <span className="text-sm text-muted-foreground">
              {t('adminProjects:stats.withForgejoRepo')}
            </span>
          </div>
          <p className="text-2xl font-bold text-warning mt-2">
            {projects.filter((p) => p.hasForgejoRepo).length}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('adminProjects:searchPlaceholder')}
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          className="pl-10"
          aria-label={t('adminProjects:searchAriaLabel')}
        />
      </div>

      {/* Projects Table */}
      {projects.length === 0 ? (
        <EmptyState
          icon={FolderGit2}
          title={t('adminProjects:empty.title')}
          description={
            search
              ? t('adminProjects:empty.searchDescription')
              : t('adminProjects:empty.description')
          }
        />
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('adminProjects:table.project')}</TableHead>
                  <TableHead>{t('adminProjects:table.owner')}</TableHead>
                  <TableHead>{t('adminProjects:table.database')}</TableHead>
                  <TableHead>{t('adminProjects:table.repo')}</TableHead>
                  <TableHead>{t('adminProjects:table.sessions')}</TableHead>
                  <TableHead>{t('adminProjects:table.created')}</TableHead>
                  <TableHead className="text-right">
                    {t('adminProjects:table.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((project) => (
                  <TableRow key={project.id}>
                    <TableCell>
                      <div className="min-w-0">
                        <Link
                          to={`/projects/${project.id}`}
                          className="text-sm font-medium hover:text-primary transition-colors"
                        >
                          {project.name}
                        </Link>
                        {project.gitUrl && (
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]">
                            {project.gitUrl.replace('https://github.com/', '')}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {project.user ? (
                        <div className="flex items-center gap-2">
                          {project.user.avatar && (
                            <img
                              src={project.user.avatar}
                              alt=""
                              className="h-6 w-6 rounded-full"
                            />
                          )}
                          <div>
                            <div className="text-sm font-medium">
                              {project.user.name || t('common:noName')}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {project.user.email}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">
                          {t('adminProjects:table.unknown')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {project.hasDatabase ? (
                        <Badge
                          variant="secondary"
                          className="gap-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                        >
                          <Database className="w-3 h-3" />
                          {t('adminProjects:table.postgresql')}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {t('adminProjects:table.none')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {project.hasForgejoRepo ? (
                        <Badge
                          variant="secondary"
                          className="gap-1 bg-warning/10 text-warning"
                        >
                          <GitBranch className="w-3 h-3" />
                          {t('adminProjects:table.forgejo')}
                        </Badge>
                      ) : project.gitUrl ? (
                        <Badge variant="secondary" className="gap-1">
                          <FolderGit2 className="w-3 h-3" />
                          {t('adminProjects:table.github')}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {t('adminProjects:table.none')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {project.activeSessionCount > 0 ? (
                          <div className="flex items-center gap-1.5">
                            <StatusIndicator status="running" size="sm" />
                            <span className="text-sm font-medium text-success">
                              {t('adminProjects:table.activeCount', {
                                count: project.activeSessionCount,
                              })}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {t('adminProjects:table.totalCount', {
                              count: project.sessionCount,
                            })}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">
                        {formatDate(project.createdAt)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        aria-label={t('adminProjects:actions.viewProject')}
                      >
                        <Link to={`/projects/${project.id}`}>
                          <ExternalLink className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-muted-foreground">
                {t('adminProjects:pagination.showing', {
                  start: (pagination.page - 1) * pagination.limit + 1,
                  end: Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  ),
                  total: pagination.total,
                })}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setPagination({ ...pagination, page: pagination.page - 1 })
                  }
                  disabled={pagination.page === 1}
                  aria-label={t('adminProjects:pagination.previous')}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">
                  {t('adminProjects:pagination.pageOf', {
                    page: pagination.page,
                    totalPages: pagination.totalPages,
                  })}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setPagination({ ...pagination, page: pagination.page + 1 })
                  }
                  disabled={pagination.page === pagination.totalPages}
                  aria-label={t('adminProjects:pagination.next')}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
